import * as THREE from "three";
import { getEdgeNormal } from "./getEdgeNormal";
import { intersectLines } from "./intersectLines";
import type { HelperLine } from "./types";

/**
 * Parallel copy of an open chain, offset by `distance` along the right normal of each edge.
 * Interior vertices land on the intersection of the two adjacent offset lines, so the corners stay
 * sharp; the two open ends are simply translated.
 *
 * Closed rings go through `offsetPolygon` instead — a naive per-edge construction cannot handle
 * the topology changes an inset causes.
 */
function offsetPolyline({ points, distance }: { points: ReadonlyArray<THREE.Vector3>; distance: number }): Array<THREE.Vector3> | null {
	if (points.length < 2) {
		return null;
	}

	const edgeLines: Array<HelperLine> = [];

	for (let edgeIndex = 0; edgeIndex < points.length - 1; edgeIndex++) {
		const normal = getEdgeNormal({ points, edgeIndex, isLoop: false });

		if (normal === null) {
			return null;
		}

		const start = points[edgeIndex];
		const end = points[edgeIndex + 1];

		edgeLines.push({
			point: start.clone().addScaledVector(normal, distance),
			direction: new THREE.Vector3(end.x - start.x, 0, end.z - start.z),
		});
	}

	const result: Array<THREE.Vector3> = [];

	for (let pointIndex = 0; pointIndex < points.length; pointIndex++) {
		const incoming = edgeLines[pointIndex - 1];
		const outgoing = edgeLines[pointIndex];

		if (incoming === undefined) {
			result.push(outgoing.point.clone().setY(points[pointIndex].y));
			continue;
		}

		if (outgoing === undefined) {
			const previous = points[pointIndex - 1];
			const offsetX = incoming.point.x - previous.x;
			const offsetZ = incoming.point.z - previous.z;
			result.push(new THREE.Vector3(points[pointIndex].x + offsetX, points[pointIndex].y, points[pointIndex].z + offsetZ));
			continue;
		}

		const corner = intersectLines({ first: incoming, second: outgoing });

		// Collinear neighbours have no corner to construct — the offset line itself is the answer.
		result.push((corner ?? outgoing.point.clone()).setY(points[pointIndex].y));
	}

	return result;
}

export { offsetPolyline };
